import React, { useState, useEffect } from "react";

import LeftPanel from "./LeftPanel";
import PartnerForm from "./PartnerForm";
import DomainExpertiseForm from "./DomainExpertiseForm";
import { becomePartner } from "../../../../services/AuthService";
import { RiShakeHandsFill } from "react-icons/ri";

export default function PartnerSignup() {
  const [showForm, setShowForm] = useState(false);
  const [step, setStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);

  // -------- Load existing saved data --------
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem("partner_application");
    return saved ? JSON.parse(saved) : {};
  });

  // -------- Clear data after 12 hours --------
  useEffect(() => {
    const saved = localStorage.getItem("partner_application");
    if (saved) {
      const parsed = JSON.parse(saved);
      const now = Date.now();
      const twelveHours = 12 * 60 * 60 * 1000;

      if (parsed.savedAt && now - parsed.savedAt > twelveHours) {
        localStorage.removeItem("partner_application");
        setFormData({});
      }
    }
  }, []);

  // -------- Clear on browser/tab close --------
  useEffect(() => {
    const clearOnClose = () => {
      localStorage.removeItem("partner_application");
    };

    window.addEventListener("beforeunload", clearOnClose);
    return () => window.removeEventListener("beforeunload", clearOnClose);
  }, []);

  // -------- Clear when success screen is shown --------
  useEffect(() => {
    if (isSuccess) {
      localStorage.removeItem("partner_application");
      setFormData({});
    }
  }, [isSuccess]);

  // -------- Update saved data --------
  const updateData = (newData) => {
    const updated = {
      ...formData,
      ...newData,
      savedAt: Date.now(),
    };

    setFormData(updated);
    localStorage.setItem("partner_application", JSON.stringify(updated));
  };

  // -------- Full Reset --------
  const clearAllData = () => {
    localStorage.removeItem("partner_application");
    setFormData({});
    setStep(1);
    setShowForm(false);
  };

  // --------------------------------------------------
  //                     SUCCESS SCREEN
  // --------------------------------------------------
  if (isSuccess) {
    return (
      <div className="partner-container">
        <LeftPanel activeStep={3} />

        <div className="partner-right success-screen">
          <div className="success-icon">
            <RiShakeHandsFill className="success-check" />
          </div>

          <h2 className="success-title">
            You have successfully registered as a Docusafe Partner.
          </h2>

          <p className="success-subtext">
            One of our experts will contact you following a thorough review.
          </p>
        </div>
      </div>
    );
  }

  // --------------------------------------------------
  //                     INITIAL SCREEN
  // --------------------------------------------------
  if (!showForm) {
    return (
      <div className="partner-container">
        <LeftPanel activeStep={1} />

        <div className="partner-right">
          <h2 className="apply-title">Steps to apply</h2>

          <div className="step-wrapper">
            <div className="step-card">
              <div className="step-icon">01</div>
              <div className="step-info">
                <h3>Create your docusafe account</h3>
                <p>You should have a valid email ID associated with an active docusafe account.</p>
              </div>
            </div>

            <div className="step-card">
              <div className="step-icon">02</div>
              <div className="step-info">
                <h3>Fill the sign-up form</h3>
                <p>Tell us more about your business and expertise.</p>
              </div>
            </div>

            <div className="step-card">
              <div className="step-icon">03</div>
              <div className="step-info">
                <h3>Application review</h3>
                <p>We will contact you after reviewing your application.</p>
              </div>
            </div>
          </div>

          <div className="get-started">You are now ready to get started!</div>

          <div className="btn-row">
            <button className="partner-btn-primary" onClick={() => setShowForm(true)}>
              Click to Apply
            </button>
          </div>

          <p className="note">
            *Kindly refrain from applying if you are an existing docusafe Affiliate or Consulting Partner.
          </p>
        </div>
      </div>
    );
  }

  // --------------------------------------------------
  //                     FORM STEPS
  // --------------------------------------------------
  return (
    <div className="partner-container">
      <LeftPanel activeStep={step} />

      <div className="partner-right">
        
        {/* STEP 1 – PartnerForm */}
        {step === 1 && (
          <PartnerForm
            initialData={formData.step1 || {}}
            onFieldChange={(updated) => updateData({ step1: updated })}
            onBack={clearAllData}
            onContinue={(data) => {
              updateData({ step1: data });
              setStep(2);
            }}
          />
        )}

        {/* STEP 2 – DomainExpertiseForm becomes FINAL STEP */}
        {step === 2 && (
          <DomainExpertiseForm
            initialData={formData.step2 || {}}
            onFieldChange={(updated) => updateData({ step2: updated })}
            onBack={() => setStep(1)}
            onContinue={async (data) => {
              updateData({ step2: data });

              // FINAL PAYLOAD
              const payload = {
                ...formData.step1,
                ...data, // domain + category + custom_fields + heard_about + captcha
              };

              // Submit API
              await becomePartner(payload);

              // clear everything
              localStorage.removeItem("partner_application");
              setFormData({});

              // show success
              setIsSuccess(true);
            }}
          />
        )}
      </div>
    </div>
  );
}
